import {
  Handshake,
  Users,
  Globe2,
  Network,
  Layers,
  Stethoscope,
  HeartPulse,
  Activity,
  ClipboardList,
  Building2,
  UserPlus,
  Share2,
  Repeat,
  ShieldCheck,
  Scale,
  Eye,
  Clock,
  Target,
  LineChart,
  FileCheck2,
  MessageSquare,
  BadgeCheck,
  Briefcase,
} from "lucide-react";

export const COMPANY = {
  name: "Azimi & Co. Vision Recruitment",
  shortName: "Azimi & Co.",
  tagline: "Exceptional talent. Precisely matched.",
  logo: "https://customer-assets-eiarnc6j.emergentagent.net/job_recruit-medical-pro/artifacts/6cguhjma_WhatsApp%20Image%202026-06-02%20at%205.18.53%20PM.jpeg",
  // Editable placeholder contact details
  email: "info@azimivision.com",
  phone: "+353 899 63 78 44",
  phoneUk: "+44 7352 75 75 30",
  address: "Dublin, Ireland | London, UK",
  hours: "Monday – Friday, 09:00 – 17:30",
};

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1685022036532-ea7e3b114578?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000",
  partnership: "https://customer-assets-eiarnc6j.emergentagent.net/job_recruit-medical-pro/artifacts/6vanlydt_pexels-shvetsa-4225921.webp",
  candidate: "https://customer-assets-eiarnc6j.emergentagent.net/job_recruit-medical-pro/artifacts/hvn8nwf9_A.webp",
  employers: "https://images.unsplash.com/photo-1706806595136-5afefb45da1a?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000",
  employersDetail: "https://images.unsplash.com/photo-1685022036574-12bffde5e2b7?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000",
  candidateDetail: "https://images.pexels.com/photos/6129502/pexels-photo-6129502.jpeg?auto=compress&cs=tinysrgb&w=2000",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Expertise", to: "/why-choose-us" },
  { label: "Solutions", to: "/services" },
  { label: "For Employers", to: "/employers" },
  { label: "For Candidates", to: "/candidates" },
  { label: "Recruitment Partnerships", to: "/partnerships" },
  { label: "Insights", to: "/resources" },
  { label: "Contact", to: "/contact" },
];

export const SERVICES = [
  {
    slug: "split-fee-recruitment",
    icon: Handshake,
    title: "Split Fee Recruitment",
    summary:
      "Collaborative placements where agencies and recruiters share candidates, roles and fees transparently.",
    description:
      "Our split fee model lets agencies and independent recruiters collaborate on healthcare placements without competing. One partner may hold the client, another the candidate — and the placement fee is shared on clearly agreed terms.",
    points: [
      "Transparent, pre-agreed fee splits",
      "Clear ownership of client and candidate relationships",
      "Simple partnership agreements",
      "Faster fills through shared talent pools",
    ],
  },
  {
    slug: "candidate-sourcing",
    icon: Users,
    title: "Candidate Sourcing & Screening",
    summary:
      "AI-assisted sourcing of qualified, compliant professionals — screened and shortlisted by our consultants.",
    description:
      "We identify, engage and pre-qualify professionals across nursing, healthcare assistant, allied health and medical support disciplines — and beyond. AI Recruitment Intelligence accelerates the search, while our consultants approve every candidate against your role, setting and compliance needs.",
    points: [
      "AI-assisted talent mapping and shortlisting",
      "Pre-screened, right-to-work aware candidates",
      "Nursing, HCA, AHP and medical support cover",
      "Precision over volume — human-approved shortlists",
    ],
  },
  {
    slug: "international-healthcare-recruitment-support",
    icon: Globe2,
    title: "International Healthcare Recruitment",
    summary:
      "Support for ethical international healthcare recruitment collaboration across borders.",
    description:
      "We work alongside partners to support international healthcare recruitment collaboration — connecting overseas talent and partners with opportunities through transparent, ethical and well-structured processes.",
    points: [
      "Ethical international recruitment collaboration",
      "Cross-border partner coordination",
      "Awareness of compliance and credentialing needs",
      "Long-term, relationship-led engagement",
    ],
  },
  {
    slug: "recruitment-partnerships",
    icon: Network,
    title: "Recruitment Partnerships",
    summary:
      "Long-term recruitment partnerships with agencies, recruiters and referral partners.",
    description:
      "We build durable recruitment partnerships built on trust and shared success — from agency collaborations and independent recruiter arrangements to referral and candidate exchange partnerships.",
    points: [
      "Agency and independent recruiter partnerships",
      "Referral and candidate exchange arrangements",
      "Clear, mutually beneficial terms",
      "Partnership Centre support",
    ],
  },
  {
    slug: "rpo-support",
    icon: Layers,
    title: "Recruitment Process Outsourcing (RPO Support)",
    summary:
      "Flexible RPO support to extend and strengthen your in-house recruitment function.",
    description:
      "Our RPO support acts as an extension of your internal team — helping manage sourcing, screening and coordination during peaks in demand or specialist campaigns, while you retain full ownership and control.",
    points: [
      "Scalable sourcing and screening support",
      "Process coordination and pipeline management",
      "Flexible engagement models",
      "You retain ownership and control",
    ],
  },
];

// Homepage "Our Expertise" cards — approved six-service set (links resolve to existing pages only)
export const HOME_SOLUTIONS = [
  {
    key: "permanent-recruitment",
    icon: Briefcase,
    title: "Permanent Recruitment",
    summary:
      "Targeted search and selection for permanent clinical and non-clinical roles, delivering professionals who strengthen your team for the long term.",
    to: "/employers#request",
  },
  {
    key: "contract-temporary-staffing",
    icon: Clock,
    title: "Contract & Temporary Staffing",
    summary:
      "Flexible cover for contract, locum and temporary requirements, giving you qualified, compliance-checked professionals when demand changes.",
    to: "/employers#request",
  },
  {
    key: "international-healthcare-recruitment",
    icon: Globe2,
    title: "International Healthcare Recruitment",
    summary:
      "Ethical, well-structured international recruitment that connects overseas healthcare professionals and partners with the right opportunities.",
    to: "/services/international-healthcare-recruitment-support",
  },
  {
    key: "candidate-sourcing-screening",
    icon: Users,
    title: "Candidate Sourcing & Screening",
    summary:
      "Structured sourcing and screening of qualified professionals, so every shortlist is relevant, consultant-reviewed and ready to interview.",
    to: "/services/candidate-sourcing",
  },
  {
    key: "compliance-credential-verification",
    icon: FileCheck2,
    title: "Compliance & Credential Verification",
    summary:
      "Rigorous review of registration, credentials and right to work, ensuring every professional presented meets the standards regulated healthcare requires.",
    to: "/why-choose-us",
  },
  {
    key: "recruitment-partnerships",
    icon: Network,
    title: "Recruitment Partnerships",
    summary:
      "Long-term collaboration with agencies, independent recruiters and referral partners on clear, mutually beneficial terms.",
    to: "/services/recruitment-partnerships",
  },
];

export const RECRUITMENT_AREAS = [
  {
    icon: HeartPulse,
    title: "Nursing",
    description:
      "Registered nurses across a range of specialisms and care settings.",
  },
  {
    icon: Stethoscope,
    title: "Healthcare Assistants",
    description:
      "Compassionate healthcare and care assistants supporting front-line care.",
  },
  {
    icon: Activity,
    title: "Allied Health Professionals",
    description:
      "Physiotherapists, radiographers, therapists and wider AHP disciplines.",
  },
  {
    icon: ClipboardList,
    title: "Medical Support Staff",
    description:
      "Administrative, coordination and support roles that keep services running.",
  },
];

export const PARTNERSHIPS = [
  {
    slug: "split-fee-partnership",
    icon: Handshake,
    title: "Split Fee Partnership",
    summary:
      "Share candidates, clients and fees on transparent, pre-agreed terms.",
    description:
      "Collaborate on placements where one partner holds the client and another the candidate. Fees are split on clearly agreed terms so both parties benefit fairly.",
  },
  {
    slug: "agency-partnership",
    icon: Building2,
    title: "Agency Partnership",
    summary:
      "Formal collaboration between recruitment agencies to extend reach.",
    description:
      "Partner agencies gain access to shared talent, complementary specialisms and additional capacity — expanding what each agency can deliver.",
  },
  {
    slug: "independent-recruiter-partnership",
    icon: UserPlus,
    title: "Independent Recruiter Partnership",
    summary:
      "Support and infrastructure for independent healthcare recruiters.",
    description:
      "Independent recruiters can collaborate with us to access roles, candidates and a trusted partnership structure while remaining independent.",
  },
  {
    slug: "referral-partnership",
    icon: Share2,
    title: "Referral Partnership",
    summary: "Refer clients or candidates and be rewarded transparently.",
    description:
      "Introduce employers or professionals to us and benefit from a clear, fair referral arrangement built on trust.",
  },
  {
    slug: "candidate-exchange-partnership",
    icon: Repeat,
    title: "Candidate Exchange Partnership",
    summary: "Exchange suitable candidates between trusted partners.",
    description:
      "Where you have candidates without roles — or roles without candidates — a candidate exchange partnership helps both sides create successful placements.",
  },
  {
    slug: "international-recruitment-partnership",
    icon: Globe2,
    title: "International Recruitment Partnership",
    summary:
      "Collaborate across borders on ethical international recruitment.",
    description:
      "Work with us to support international healthcare recruitment collaboration through transparent, well-structured and ethical processes.",
  },
];

export const WHY_CHOOSE_US = [
  {
    icon: Target,
    title: "Healthcare-First Specialism",
    description:
      "Recruitment led by people who understand clinical settings — from nursing and AHP to medical support and beyond.",
  },
  {
    icon: BadgeCheck,
    title: "AI Recruitment Intelligence",
    description:
      "AI-assisted sourcing, screening and matching accelerate every search — always reviewed and approved by our consultants.",
  },
  {
    icon: ShieldCheck,
    title: "Trust & Transparency",
    description:
      "Clear terms, honest communication and no unsupported claims — every engagement is built on transparency.",
  },
  {
    icon: Scale,
    title: "Compliance by Design",
    description:
      "A rigorous, compliance-aware approach to screening, credentialing and right-to-work, built for regulated healthcare.",
  },
  {
    icon: Globe2,
    title: "Multi-Sector Ready",
    description:
      "The elite standards we set in healthcare extend to structured talent solutions across every professional sector.",
  },
  {
    icon: Handshake,
    title: "Long-Term Partnerships",
    description:
      "We invest in relationships that last, working with clients, candidates and partners as long-term collaborators.",
  },
];

export const TRUST_VALUES = [
  { icon: ShieldCheck, label: "Trusted Partnerships" },
  { icon: Eye, label: "Transparent Recruitment" },
  { icon: BadgeCheck, label: "Specialist Expertise" },
  { icon: Handshake, label: "Strategic Collaboration" },
  { icon: LineChart, label: "Long-Term Value" },
];

export const EMPLOYER_JOURNEY = [
  { title: "Request Talent", description: "Tell us the roles, setting and standards you need." },
  { title: "Consultation", description: "We discuss requirements, timelines and expectations." },
  { title: "Recruitment Process", description: "We source, screen and shortlist matched professionals." },
  { title: "Placement", description: "The right professional joins your team, with ongoing support." },
];

export const CANDIDATE_JOURNEY = [
  { title: "Submit CV", description: "Share your CV and details securely with our team." },
  { title: "Screening", description: "We review your experience and career preferences." },
  { title: "Compliance Review", description: "We support the compliance and right-to-work process." },
  { title: "Interview Support", description: "We help you prepare and coordinate interviews." },
  { title: "Opportunity Matching", description: "We introduce you to suitable roles that fit your goals." },
];

export const ROADMAP = [
  "Employer Portal",
  "Candidate Portal",
  "Partner Portal",
  "Recruiter Portal",
  "Client Dashboard",
  "Candidate Dashboard",
  "Online Interview Booking",
  "Document Upload Centre",
  "ATS Integration",
  "CRM Integration",
  "Job Board",
  "AI Candidate Matching",
  "Compliance Portal",
  "Timesheet Portal",
  "Invoice Portal",
  "API Integration",
  "Mobile App",
];

export const FUTURE_ENTERPRISE = [
  "White Label Recruitment Support",
  "Managed Recruitment Services",
  "MSP",
  "VMS",
  "Compliance Management",
  "Payroll Coordination",
  "Reference Checking",
  "Credential Verification",
  "Candidate Onboarding",
  "Healthcare Workforce Management",
];

export const RESOURCES = [
  {
    icon: FileCheck2,
    title: "Working With a Recruitment Partner",
    description:
      "How split fee and recruitment partnerships work, and what to expect when collaborating with us.",
  },
  {
    icon: Users,
    title: "Guidance for Healthcare Professionals",
    description:
      "Preparing your CV, understanding compliance, and getting the most from interview support.",
  },
  {
    icon: Building2,
    title: "Guidance for Employers",
    description:
      "How to brief a role effectively and build a productive recruitment partnership.",
  },
  {
    icon: MessageSquare,
    title: "Frequently Asked Questions",
    description:
      "Answers to common questions about our services, partnerships and processes.",
  },
];

export const FAQS = [
  {
    q: "Are you healthcare specialists or a general recruiter?",
    a: "We are healthcare-first. Our expertise is built around clinical settings — nursing, healthcare assistants, allied health and medical support. The same standards and intelligence also power structured talent solutions for other professional sectors.",
  },
  {
    q: "What is AI Recruitment Intelligence?",
    a: "It is the technology that assists our team with sourcing, CV screening, compliance checks and candidate matching. Every AI recommendation is reviewed and approved by an experienced consultant before it reaches a client.",
  },
  {
    q: "How does split fee recruitment work?",
    a: "One partner may hold the client relationship while another holds the candidate. The placement fee is shared on clearly agreed, transparent terms.",
  },
  {
    q: "Do you support international recruitment?",
    a: "Yes. We support ethical international healthcare recruitment collaboration through transparent and well-structured processes with our partners.",
  },
  {
    q: "How is my data handled?",
    a: "We handle personal data with care and in a GDPR-aware manner, and you retain ownership of your information. Please see our Privacy Policy and GDPR Privacy Notice for details.",
  },
];

export const LEGAL_PAGES = [
  { slug: "privacy-policy", title: "Privacy Policy" },
  { slug: "cookie-policy", title: "Cookie Policy" },
  { slug: "terms-of-business", title: "Terms of Business" },
  { slug: "employer-terms", title: "Employer Terms" },
  { slug: "candidate-terms", title: "Candidate Terms" },
  { slug: "recruitment-partner-terms", title: "Recruitment Partner Terms" },
  { slug: "gdpr-privacy-notice", title: "GDPR Privacy Notice" },
  { slug: "equal-opportunities-policy", title: "Equal Opportunities Policy" },
  { slug: "complaints-procedure", title: "Complaints Procedure" },
  { slug: "accessibility-statement", title: "Accessibility Statement" },
];

// Curated footer navigation for a cleaner, premium enterprise layout
export const FOOTER_EXPLORE = [
  { label: "About Us", to: "/about" },
  { label: "Our Expertise", to: "/why-choose-us" },
  { label: "Talent Solutions", to: "/services" },
  { label: "Hire Talent", to: "/employers" },
  { label: "Jobseekers", to: "/candidates" },
  { label: "Strategic Alliances", to: "/partnerships" },
  { label: "Resources", to: "/resources" },
  { label: "Contact", to: "/contact" },
];

export const FOOTER_GROUPS = [
  {
    title: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Expertise", to: "/why-choose-us" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    title: "Solutions",
    links: [
      { label: "Permanent Recruitment", to: "/employers#request" },
      { label: "Contract & Temporary Staffing", to: "/employers#request" },
      { label: "International Healthcare Recruitment", to: "/services/international-healthcare-recruitment-support" },
      { label: "Compliance & Credential Verification", to: "/why-choose-us" },
    ],
  },
  {
    title: "For Talent",
    links: [
      { label: "Explore Opportunities", to: "/candidates" },
      { label: "Submit Your CV", to: "/candidates#submit" },
    ],
  },
  {
    title: "Partnerships",
    links: [{ label: "Recruitment Partnerships", to: "/partnerships" }],
  },
  {
    title: "Resources",
    links: [
      { label: "Insights", to: "/resources" },
      { label: "Resources", to: "/resources" },
    ],
  },
];

export const FOOTER_LEGAL = [
  "privacy-policy",
];

export const ORGANISATION_TYPES = [
  "Hospital",
  "Nursing Home",
  "Care Home",
  "Healthcare Provider",
  "Healthcare Organisation",
  "Clinic / Practice",
  "Other",
];

export const PROFESSIONS = [
  "Registered Nurse",
  "Healthcare Assistant",
  "Allied Health Professional",
  "Medical Support Staff",
  "Other",
];

export const PARTNER_TYPES = [
  "Recruitment Agency",
  "Independent Recruiter",
  "Referral Partner",
  "International Recruitment Partner",
  "Candidate Exchange Partner",
];

export const MEDICAL_SPECIALTIES = [
  "General / Ward", "ICU / Critical Care", "Theatre / Perioperative", "Emergency",
  "Paediatrics", "Elderly / Care of the Elderly", "Mental Health", "Oncology",
  "Community", "Radiography", "Physiotherapy", "Occupational Therapy", "Other",
];

export const CONTRACT_TYPES = ["Permanent", "Fixed Term", "Temporary / Agency", "Locum", "Part-Time"];
export const EXPERIENCE_LEVELS = ["0–2 years", "3–5 years", "6–10 years", "10+ years"];
export const CLIENT_TYPES = ["Hospital (Public)", "Hospital (Private)", "Nursing Home", "Care Home", "Clinic / Practice", "Healthcare Group", "Other"];

export const PLACEMENT_TYPES = ["Permanent", "Temporary / Locum (Shift Cover)", "Contract"];
export const FACILITY_TYPES = ["Acute Hospital", "Community Hospital", "Nursing Home", "Care Home", "Clinic / Practice", "Mental Health Unit", "Home Care", "Non-Clinical / Other"];
export const SOURCING_SCOPES = ["National (Local)", "International"];
export const CERTIFICATION_OPTIONS = ["ACLS", "BLS", "PALS", "ILS", "ATLS", "NRP", "Manual Handling", "IV Cannulation", "Venepuncture"];
export const WORK_RIGHTS_OPTIONS = ["Stamp 4", "EU", "Right to Work", "Visa Required"];
export const REGISTRATION_BODIES = ["NMBI (Ireland)", "NMC (UK)", "GMC (UK)", "HCPC (UK)", "CORU (Ireland)", "Not Applicable", "Other"];
export const LANGUAGE_OPTIONS = ["English", "Irish", "French", "German", "Spanish", "Arabic", "Hindi", "Tagalog", "Other"];

export const CANDIDATE_STAGES = [
  "New Candidate", "Profile Review", "Qualified Candidate", "Recruiter Review",
  "Submitted to Client", "Interview Stage", "Hired",
];
export const JOB_STAGES = [
  "New Job Request", "Searching Candidates", "Candidates Submitted",
  "Interview Stage", "Filled", "Closed",
];
